import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import downloadJson from "utils/downloadFile";
import apiRequest from "utils/apiRequestWithErrorCode";
import { ChevronRight } from "@material-ui/icons";
import {
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import DetailItem from "components/EmailEventDetails/DetailItem";

import { EmailFlowLog, EmailFlowLogDetails } from "@dashboard-v3/api";

type ActionsState = {
  loading: boolean;
  expanded: boolean;
  actions: EmailFlowLogDetails[];
};

interface Props {
  email: EmailFlowLog;
}

export default function EmailActions(props: Props) {
  const { email } = props;
  const { t } = useTranslation("emailFlowLogs");
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState<ActionsState>(initActions);
  const classes = useStyles(state.expanded);
  const [eventDetails, setEventDetails] = useState({
    isOpen: false,
    event: null,
  });

  function updateEmailActions(update: Partial<ActionsState>) {
    setState(prev => ({ ...prev, ...update }));
  }

  async function fetchEmailActions() {
    const { actions } = state;
    if (actions.length) return;

    updateEmailActions({ loading: true });
    try {
      const { msgUID } = email;
      const result = await apiRequest<EmailFlowLogDetails[]>(
        "GET",
        `/emailflow-logs/${msgUID}/actions`
      );
      updateEmailActions({ actions: result });
    } catch (error) {
      enqueueSnackbar(t("error.fetchItemEvents"), {
        variant: "error",
      });
    } finally {
      updateEmailActions({ loading: false });
    }
  }

  function getDate(timestamp: number) {
    const format = "MMM D, YYYY, h:mm:ss A";
    return dayjs(timestamp).format(format);
  }

  function onDownload() {
    downloadJson(
      state.actions,
      t("emailDetails.downloadName", { id: email.msgUID })
    );
  }

  function onExpand() {
    updateEmailActions({ expanded: !state.expanded });
    fetchEmailActions();
  }

  function handleDetails(isOpen: boolean, event?: EmailFlowLogDetails) {
    if (isOpen && !event) {
      return console.error("Can't open details without an event to show");
    }

    return setEventDetails({ isOpen, event: event || null });
  }

  return (
    <>
      <div className={classes.titleContainer} onClick={onExpand}>
        <Typography variant="subtitle2">
          {t("emailDetails.actiion.title")}
        </Typography>
        <ChevronRight fontSize="small" className={classes.titleIcon} />
      </div>
      <Collapse className={classes.dataContainer} in={state.expanded}>
        {state.loading ? (
          <Grid container spacing={2}>
            {[1, 2, 3].map(item => (
              <Grid item xs={12} key={item}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Skeleton
                      variant="text"
                      width="30%"
                      height={16}
                      style={{ marginBottom: 4 }}
                    />
                    <Skeleton variant="text" width="70%" height={16} />
                  </Grid>
                  <Grid item xs={6}>
                    <Skeleton
                      variant="text"
                      width="40%"
                      height={16}
                      style={{ marginBottom: 4 }}
                    />
                    <Skeleton variant="text" width="80%" height={16} />
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
        ) : state.actions.length > 0 ? (
          <Grid container spacing={2}>
            {state.actions.map((item, i, actions) => (
              <Grid item xs={12} key={item.eventId}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      {t("emailDetails.createdAt.label")}
                    </Typography>
                    <Typography variant="body2">
                      {getDate(item.createdAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      {t("emailDetails.actiion.label")}
                    </Typography>
                    <Typography variant="body2">
                      {t(`eventActionType.${item.action}`)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      {t("emailDetails.details.label")}
                    </Typography>
                    {item.action === "mail_split" ? (
                      <Typography variant="body2" color="textSecondary">
                        {t("emailDetails.details.unavailable")}
                      </Typography>
                    ) : (
                      <Link
                        component="button"
                        variant="body2"
                        color="primary"
                        onClick={() => handleDetails(true, item)}
                      >
                        {t("emailDetails.details.viewAction")}
                      </Link>
                    )}
                  </Grid>
                </Grid>
                {i !== actions.length - 1 && (
                  <Divider className={classes.itemsDivider} />
                )}
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="textSecondary">
            {t("emailDetails.emptyEmailAction")}
          </Typography>
        )}
        <Button
          className={classes.downloadBtn}
          variant="outlined"
          color="primary"
          disableElevation
          onClick={onDownload}
          disabled={state.loading}
        >
          {t("common:download")}
        </Button>
      </Collapse>
      {eventDetails.isOpen && (
        <Dialog
          open={eventDetails.isOpen}
          onClose={() => handleDetails(false)}
          fullWidth
          maxWidth="md"
        >
          <IconButton
            size="small"
            className={classes.closeBtn}
            onClick={() => handleDetails(false)}
          >
            <CloseIcon />
          </IconButton>
          <DialogTitle className={classes.dialogTitle}>
            {t("emailDetails.details.dialogTitle")}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <Grid container spacing={2} className={classes.detailsContainer}>
              <DetailItem
                recipient={email.sender}
                event={eventDetails.event}
                includeAll
                showSplitRecipientStatus
              />
            </Grid>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function initActions() {
  return {
    loading: false,
    expanded: false,
    actions: [],
  };
}

const useStyles = makeStyles({
  titleContainer: {
    marginTop: 10,
    display: "flex",
    gap: 5,
    alignItems: "center",
    cursor: "pointer",
  },
  titleIcon: { rotate: expanded => (expanded ? "90deg" : "") },
  dataContainer: { marginLeft: 25, maxWidth: "80%" },
  itemsDivider: { marginTop: 20 },
  downloadBtn: { marginTop: 30, marginBottom: 10, alignSelf: "flex-start" },
  closeBtn: {
    position: "absolute",
    top: "0.6rem",
    right: "0.6rem",
  },
  dialogTitle: { paddingBottom: 0 },
  dialogContent: { paddingTop: 0, paddingBottom: 30 },
  detailsContainer: { marginTop: -16 },
});
